import { NAME_HEADER_AUTH } from '../utils/Constants';
import getStoredValue from '../services/UseLocalStorage';

const PAGE_CLONER_API = (
    process.env.REACT_APP_PAGE_CLONER_API ||
    'https://pulpoia-ops.com/backend/backend-page-cloner'
).replace(/\/$/, '');

const GENERATOR_API = (
    process.env.REACT_APP_CLONE_API ||
    //'http://127.0.0.1:8000'
    'https://pulpoia-ops.com/backend/ultimate-cloner'
).replace(/\/$/, '');

const CLONE_CONTROLLER_API = (
    process.env.REACT_APP_CLONE_CONTROLLER_API ||
    'https://pulpoia-ops.com/backend/ultimate-cloner'
).replace(/\/$/, '');

const authToken = () => getStoredValue(NAME_HEADER_AUTH) || '';

const jsonHeaders = () => ({
    Accept: 'application/json',
    'Content-Type': 'application/json',
});

const readResponse = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (response.ok) return data;

    const detail = typeof data.detail === 'string'
        ? data.detail
        : data.detail?.message || data.message || response.statusText;
    throw new Error(detail || `Request failed with status ${response.status}`);
};

const postJson = async (path, payload) => {
    const response = await fetch(`${PAGE_CLONER_API}${path}`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
    });
    return { ...(await readResponse(response)), ok: true };
};

export const fetchLandingPages = async (page, pageSize) => {
    const query = new URLSearchParams({
        token: authToken(),
        page: String(page),
        page_size: String(pageSize),
    });
    const response = await fetch(`${PAGE_CLONER_API}/api/v1/landings-buckets?${query}`);
    return readResponse(response);
};

export const fetchUserDomains = async () => {
    const query = new URLSearchParams({ token: authToken() });
    const response = await fetch(`${PAGE_CLONER_API}/api/v1/domains?${query}`);
    return readResponse(response);
};

export const prepareResources = async ({ domain, domainMode = 'NONE', subdomain = '' }) => {
    const token = authToken();
    if (!token) throw new Error('No se encontró el token del usuario');

    const payload = { Token: token };
    const normalizedDomain = typeof domain === 'string' ? domain.trim() : '';
    if (domainMode === 'PLATFORM') {
        payload.DomainMode = 'PLATFORM';
        payload.Subdomain = String(subdomain || '').trim();
    } else if (normalizedDomain && normalizedDomain.toLowerCase() !== 'ninguno') {
        payload.DomainMode = 'CUSTOM';
        payload.Dominio = normalizedDomain;
    }

    const preparation = await postJson('/api/v1/prepare', payload);
    if (!preparation.workspace?.BucketName) {
        throw new Error('La preparación no devolvió un workspace válido');
    }

    return {
        ...preparation,
        domain: preparation.domain || null,
        domainRequired: Boolean(preparation.domainRequired),
        domainCreated: Boolean(preparation.domainCreated),
    };
};

export const createLanding = async ({ name, sourceUrl, domainId }) => {
    const payload = {
        Token: authToken(),
        Nombre: name,
        URL: sourceUrl,
    };
    if (domainId != null) payload.FK_ID_Dominios_Usuarios = domainId;
    return postJson('/api/v1/landings', payload);
};

export const updateLandingUrls = async ({ landingUuid, s3Url, cloudFrontUrl, status = 'READY', error = null }) => {
    const response = await fetch(`${PAGE_CLONER_API}/api/v1/landings/url-s3`, {
        method: 'PUT',
        headers: jsonHeaders(),
        body: JSON.stringify({
            Token: authToken(),
            uuid_landing: landingUuid,
            URL_s3_final: s3Url || '',
            URL_cloudFront_final: cloudFrontUrl || '',
            CloneStatus: status,
            CloneError: error,
        }),
    });
    return { ...(await readResponse(response)), ok: true };
};

export const deactivateLanding = async (landingUuid) => {
    const response = await fetch(`${PAGE_CLONER_API}/api/v1/landings/deactivate`, {
        method: 'PUT',
        headers: jsonHeaders(),
        body: JSON.stringify({
            Token: authToken(),
            uuid_landing: landingUuid,
        }),
    });
    return readResponse(response);
};

export const startClone = async ({ sourceUrl, productName, productPrice, landingUuid, workspace, s3Prefix, cloudfrontDomain }) => {
    const token = authToken();
    if (!token) throw new Error('No se encontró el token del usuario');

    const response = await fetch(`${CLONE_CONTROLLER_API}/clone`, {
        method: 'POST',
        headers: {
            ...jsonHeaders(),
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            url: sourceUrl,
            product_name: productName,
            price: productPrice,
            landing_uuid: landingUuid,
            bucket_name: workspace?.BucketName,
            s3_prefix: s3Prefix,
            cloudfront_domain: cloudfrontDomain || workspace?.URL_CloudFront,
        }),
    });
    return readResponse(response);
};

export const pollClone = async (jobId, { intervalMs = 5000, timeoutMs = 35 * 60 * 1000 } = {}) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        const response = await fetch(`${GENERATOR_API}/status/${encodeURIComponent(jobId)}`);
        const data = await readResponse(response);
        if (data.stage === 'done') return data;
        if (data.stage === 'error') throw new Error(data.error || 'El clonado falló');
    }
    throw new Error('La generación está tardando más de lo esperado. El proceso puede seguir en curso.');
};
